import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plug, Copy, Check, Sparkles, Building2, ShieldCheck, ArrowRight, Bot } from "lucide-react";

// Guide d'intégration MCP : comment connecter Claude (ou un autre client MCP) à Acteio
// pour piloter les propositions en langage naturel.
const TOOLS = [
  { n: "acteio_get_dashboard", d: "KPIs et statistiques du tableau de bord" },
  { n: "acteio_list_proposals", d: "Lister les propositions (filtres statut/recherche)" },
  { n: "acteio_get_proposal", d: "Détail complet d'une proposition" },
  { n: "acteio_get_analytics", d: "Analytics d'engagement d'une proposition" },
  { n: "acteio_create_proposal", d: "Créer une proposition avec ses sections" },
  { n: "acteio_update_proposal", d: "Modifier titre, sections ou devise" },
  { n: "acteio_send_proposal", d: "Envoyer la proposition au prospect" },
  { n: "acteio_list_clients", d: "Lister les clients / prospects" },
  { n: "acteio_create_client", d: "Créer un client / prospect" },
  { n: "acteio_generate_ai_content", d: "Générer le contenu d'une proposition par IA" },
  { n: "acteio_generate_template", d: "Générer un modèle pour un secteur" },
  { n: "acteio_list_companies", d: "Lister les sociétés accessibles" },
];

export default function Mcp() {
  const { brand } = useAuth();
  const [copied, setCopied] = useState(false);
  const mcpUrl = `${window.location.origin}/mcp`;
  const isPro = ["pro", "business", "enterprise"].includes(brand?.effective_plan || brand?.plan);

  const copy = () => {
    navigator.clipboard.writeText(mcpUrl);
    setCopied(true);
    toast.success("URL du connecteur copiée");
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Intégration · {brand?.name}</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Plug className="w-8 h-8 text-primary" /> Connecter Claude (MCP)
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Pilotez vos propositions, clients et analytics <strong>en langage naturel</strong> depuis Claude.
          Le connecteur MCP relie votre espace Acteio à Claude en toute sécurité (OAuth).
        </p>
      </div>

      {!isPro && (
        <div className="mb-6 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="mcp-plan-notice">
          <span className="font-semibold">Fonctionnalité Pro.</span> Le connecteur MCP nécessite un forfait Pro ou supérieur.{" "}
          <Link to="/billing" className="underline underline-offset-2 font-medium">Voir les forfaits →</Link>
        </div>
      )}

      {/* URL du connecteur */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">URL du connecteur distant</h3>
        </div>
        <div className="flex items-stretch gap-2">
          <code className="flex-1 bg-muted rounded-sm px-4 flex items-center font-mono text-sm break-all" data-testid="mcp-url">{mcpUrl}</code>
          <button onClick={copy} data-testid="mcp-copy"
            className="flex items-center gap-2 border border-border px-4 h-11 rounded-sm text-sm font-medium hover:border-primary hover:text-primary transition-all shrink-0">
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {copied ? "Copié" : "Copier"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">C'est l'adresse à coller dans le client Claude pour ajouter Acteio comme connecteur.</p>
      </div>

      {/* Étapes claude.ai */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-5">Ajouter le connecteur dans Claude</h3>
        <ol className="space-y-4">
          {[
            <>Dans <strong>claude.ai</strong> (ou Claude Desktop) : <strong>Settings → Connectors → Add custom connector</strong>.</>,
            <>Collez l'URL ci-dessus (<code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{mcpUrl}</code>) et validez.</>,
            <>Vous êtes redirigé vers <strong>la page de connexion Acteio</strong> — connectez-vous (ou, si vous êtes déjà connecté, vous passez directement à l'étape suivante).</>,
            <>Choisissez la <strong>société</strong> à piloter — ou <strong>« 🌐 Toutes mes sociétés éligibles »</strong> pour toutes les gérer depuis un seul connecteur — puis <strong>Autoriser</strong>.</>,
            <>Les outils <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">acteio_*</code> apparaissent dans Claude. C'est prêt !</>,
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary font-semibold text-sm flex items-center justify-center">{i + 1}</span>
              <span className="text-sm text-foreground/90 leading-relaxed pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Multi-société */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Plusieurs sociétés</h3>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Un seul connecteur peut piloter toutes vos sociétés : choisissez <strong>« Toutes mes sociétés éligibles »</strong> au moment
          d'autoriser. Ensuite, précisez la société dans votre demande — par ex. <em>« le tableau de bord de {brand?.slug || "oxiwi"} »</em>.
          Vous pouvez aussi ré-autoriser le connecteur pour le figer sur une seule société.
        </p>
      </div>

      {/* Exemples */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Exemples de demandes</h3>
        </div>
        <ul className="space-y-2">
          {[
            `Donne-moi le tableau de bord de ${brand?.slug || "oxiwi"}`,
            "Liste mes propositions en attente et leur statut",
            "Crée une proposition pour [client] : [décris le besoin, le budget]",
            "Quelles sont les analytics de la proposition « [titre] » ?",
            "Rédige le contenu d'une proposition à partir de ce transcript d'appel…",
          ].map((ex, i) => (
            <li key={i} className="text-sm bg-muted/60 rounded-sm px-3 py-2 text-foreground/90 italic">« {ex} »</li>
          ))}
        </ul>
      </div>

      {/* Outils */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4">Outils exposés ({TOOLS.length})</h3>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {TOOLS.map((t) => (
            <div key={t.n} className="flex flex-col">
              <code className="font-mono text-xs text-primary">{t.n}</code>
              <span className="text-xs text-muted-foreground">{t.d}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sécurité */}
      <div className="flex items-start gap-3 text-sm text-muted-foreground bg-card border border-border rounded-sm p-5">
        <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        <p>
          <strong className="text-foreground">Sécurisé.</strong> L'accès passe par une autorisation OAuth : Claude n'obtient jamais votre mot de passe,
          l'accès est limité à la société autorisée et reste soumis à vos permissions (RBAC). Vous pouvez révoquer le connecteur à tout moment dans Claude.
        </p>
      </div>
    </div>
  );
}
